import {
  Alignment,
  Classes,
  Label,
  PanelProps,
  Position,
  Switch,
} from '@blueprintjs/core';
import { createSelector } from '@reduxjs/toolkit';
import * as React from 'react';
import { Tooltip } from 'vet-bones/components';
import { isDevelopment } from 'vet-bones/utils';
import { usePortalFeatures } from 'vet-bones/utils/portal';

import { DatabaseSelector } from 'src/components/DatabaseSelector';
import { TEST_IDS } from 'src/constants/testIds';
import { IntersectedEmptyObject } from 'src/constants/types';
import { useValidateSensitivePropertyGroupPublishName } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useSensitivePropertyGroups';
import {
  useDefaultSPGInfo,
  useRefreshDatabases,
} from 'src/containers/PublishProjectDrawer/hooks';
import {
  ModelConfigPanel,
  ModelConfigPanelProps,
} from 'src/containers/PublishProjectDrawer/ModelConfigPanel';
import { ModelConfigSPG } from 'src/containers/PublishProjectDrawer/ModelConfigSPG';
import { PublishProjectDrawerFooter } from 'src/containers/PublishProjectDrawer/PublishProjectDrawerFooter';
import { PublishProjectDrawerStepper } from 'src/containers/PublishProjectDrawer/PublishProjectDrawerStepper';
import {
  CreationType,
  DatabaseInfo,
  SensitivePropertyGroupsInfo,
} from 'src/containers/PublishProjectDrawer/types';
import { catalogDatabaseSelector } from 'src/selectors/catalogDatabase';
import { databaseSelector } from 'src/selectors/database';
import { isDatabaseRefreshedSelector } from 'src/selectors/isRefreshingDatabase';
import { projectSelector } from 'src/selectors/project';
import { projectCompetencyQuestionsSelector } from 'src/selectors/projectCompetencyQuestions';
import { projectModelsByIdSelector } from 'src/selectors/projectModelsById';
import { projectPublishHistorySelector } from 'src/selectors/projectPublishHistory';
import { querylogDatabaseSelector } from 'src/selectors/querylogDatabase';
import { setupPortalConnection } from 'src/store/connections/connectToStardog';
import { ProjectModel } from 'src/store/state/projects';
import { ReduxState } from 'src/store/types';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { useDefaultPortalConnection } from 'src/utils/hooks/useDefaultPortalConnection';
import { useDesignerPermissions } from 'src/utils/hooks/useDesignerPermissions';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { replaceInvalidIriChars } from 'src/utils/removeInvalidIriChars';
import { useEvaluateQuestions } from 'src/utils/voicebox/hooks/useEvaluateQuestions';

export const getDefaultNamespacePrefix = (model?: ProjectModel) =>
  replaceInvalidIriChars(model?.name);

export const getDefaultNamespaceIri = (model: ProjectModel) =>
  model?.namespace || '';

const isDatabaseReadySelector = (databaseId: string | null) =>
  createSelector(
    databaseSelector(databaseId),
    isDatabaseRefreshedSelector(databaseId),
    (database, isRefreshed) => Boolean(!database || isRefreshed)
  );

export const DatabaseConfigPanel: React.VFC<
  PanelProps<IntersectedEmptyObject>
> = ({ openPanel }) => {
  const { projectId } = useRouterParams();
  const dispatch = useReduxDispatch();
  const defaultPortalConnection = useDefaultPortalConnection();
  const project = useReduxSelector(projectSelector(projectId));
  const modelsById = useReduxSelector(projectModelsByIdSelector(projectId));
  const { canCreateDatabase } = useDesignerPermissions();

  const voiceboxDbLimit = useReduxSelector(
    (state) =>
      state.connections.voiceboxDbLimitByConnectionId[
        defaultPortalConnection?.id || ''
      ] || 0
  );
  const databasesById = useReduxSelector(
    (state) => state.databases.databasesById
  );
  const voiceboxDatabaseIds = useReduxSelector(
    createSelector(
      catalogDatabaseSelector,
      querylogDatabaseSelector,
      (state: ReduxState) => state.databases.databasesById,
      (catalogDatabaseId, querylogDatabaseId, databasesById) =>
        new Set(
          Object.values(databasesById)
            .filter(
              (database) =>
                database.isVoiceboxEnabled &&
                // ignore the catalog database
                database.id !== catalogDatabaseId &&
                // ignore the querylog database
                database.id !== querylogDatabaseId
            )
            .map((database) => database.id)
        )
    )
  );

  const [databaseId, setDatabaseId] = React.useState<string>('');
  const didDatabaseChange = useDidValueChange(databaseId);
  const isDatabaseReady = useReduxSelector(isDatabaseReadySelector(databaseId));
  const refreshedDatabase = useReduxSelector(
    isDatabaseRefreshedSelector(databaseId)
  );
  const didRefreshedDatabaseChange = useDidValueChange(refreshedDatabase);
  useRefreshDatabases({ databaseId, setDatabaseId });

  const portalFeatures = usePortalFeatures();
  const isVoiceboxEnabledUser = portalFeatures.voicebox || isDevelopment();
  const isVoiceboxEnabledServer = Boolean(voiceboxDbLimit);
  const isVoiceboxToggleVisible = Boolean(
    databaseId && isVoiceboxEnabledUser && isVoiceboxEnabledServer
  );

  const existingDatabase = (databaseId && databasesById[databaseId]) || null;
  const isVoiceboxEnabledDatabase = existingDatabase?.isVoiceboxEnabled;

  const canToggleVoicebox =
    Boolean(databaseId) &&
    !existingDatabase &&
    voiceboxDatabaseIds.size < voiceboxDbLimit;

  const [shouldEnableVoicebox, setShouldEnableVoicebox] = React.useState(true);
  const isVoiceboxEnabledChecked =
    isVoiceboxEnabledDatabase || (canToggleVoicebox && shouldEnableVoicebox);

  const questions = useReduxSelector(
    projectCompetencyQuestionsSelector(projectId)
  );

  const isVoiceboxQuestionsToggleVisible = Boolean(
    isVoiceboxToggleVisible &&
      isVoiceboxEnabledChecked &&
      portalFeatures.voiceboxSuggestions &&
      questions.length
  );

  const [shouldPublishVoiceboxQuestions, setShouldPublishVoiceboxQuestions] =
    React.useState(
      project?.publishHistory.databaseInfo.isPublishingVoiceboxQuestions ?? true
    );
  const isVoiceboxQuestionsChecked =
    isVoiceboxQuestionsToggleVisible && shouldPublishVoiceboxQuestions;
  const { evaluateQuestions, isLoadingEvaluations, isLoadingQueries } =
    useEvaluateQuestions();
  const isLoadingQuestions = isLoadingEvaluations || isLoadingQueries;
  const unevaluatedQuestions = questions.filter(
    (question) =>
      !question.evaluation?.query ||
      !project?.voiceboxEvaluatedCompetencyQuestions?.includes(question.id)
  );
  const didEvaluateQuestions = React.useRef(false);

  // Auto-trigger evaluation when voicebox questions toggle is enabled
  React.useEffect(() => {
    if (
      isVoiceboxQuestionsToggleVisible &&
      shouldPublishVoiceboxQuestions &&
      unevaluatedQuestions.length &&
      !isLoadingQuestions &&
      !didEvaluateQuestions.current
    ) {
      didEvaluateQuestions.current = true;
      evaluateQuestions(unevaluatedQuestions, {
        waitForQueries: true,
      });
    }
  }, [
    shouldPublishVoiceboxQuestions,
    isVoiceboxQuestionsToggleVisible,
    unevaluatedQuestions,
    isLoadingQuestions,
    evaluateQuestions,
  ]);

  const getDefaultSPGInfo = useDefaultSPGInfo(databaseId);
  const initialSPGInfo = getDefaultSPGInfo();
  const [spgInfo, setSPGInfo] = React.useState(initialSPGInfo);
  const [isPublishingSPG, setIsPublishingSPG] = React.useState(
    project?.publishHistory.databaseInfo.isPublishingSPG ?? true
  );
  const [spgError, setSpgError] = React.useState<string | null>(null);

  const didConnection = React.useRef(false);

  const publishHistory = useReduxSelector(
    projectPublishHistorySelector(projectId)
  );

  // Select default connection from publish history, or default portal connection
  React.useEffect(() => {
    if (didConnection.current || !defaultPortalConnection) {
      return;
    }
    didConnection.current = true;

    dispatch(setupPortalConnection(defaultPortalConnection));
  }, [defaultPortalConnection, didConnection, dispatch, publishHistory]);

  // Update default namespace IRI on database change
  React.useEffect(() => {
    if (!project || !didDatabaseChange) {
      return;
    }

    const { isPublishingSPG } = project.publishHistory.databaseInfo;

    setIsPublishingSPG(isPublishingSPG);
  }, [didDatabaseChange, modelsById, project]);

  // Set SPG info from updated database
  React.useEffect(() => {
    if (
      (didDatabaseChange || didRefreshedDatabaseChange) &&
      refreshedDatabase
    ) {
      setSPGInfo(initialSPGInfo);
    }
  }, [
    didDatabaseChange,
    didRefreshedDatabaseChange,
    initialSPGInfo,
    project,
    refreshedDatabase,
  ]);

  const validateSPGCreateName = useValidateSensitivePropertyGroupPublishName(
    databaseId,
    spgInfo
  );

  const handleUpdateSPG = React.useCallback(
    (update: SensitivePropertyGroupsInfo) => {
      setSPGInfo(update);
      let publishError = '';
      if (isPublishingSPG) {
        Object.values(update).forEach((spg) => {
          if (spg.actionType === CreationType.NEW) {
            const error = validateSPGCreateName(spg);
            if (error) {
              publishError = error;
            }
          } else if (spg.publishName === '') {
            publishError =
              copy.components.publishProjectDrawer.modelConfiguration.sensitivePropertyGroups.replaceOrAppendUnnamedGroup(
                spg.spgName
              );
          }
        });
      }
      setSpgError(publishError);
    },
    [isPublishingSPG, setSPGInfo, setSpgError, validateSPGCreateName]
  );

  const nextButtonError = React.useMemo(() => {
    if (isPublishingSPG && spgError) {
      return spgError;
    }
    if (!databaseId) {
      return copy.components.publishProjectDrawer.validation.database;
    }
    if (!isDatabaseReady) {
      return copy.components.publishProjectDrawer.validation.refreshDatabase;
    }
    return '';
  }, [databaseId, isDatabaseReady, isPublishingSPG, spgError]);

  const handleCancel = React.useCallback(() => {
    dispatch(uiSlice.actions.toggledPublishProjectDrawer({ isOpen: false }));
  }, [dispatch]);

  const handleNext = React.useCallback(() => {
    if (!databaseId || nextButtonError) {
      return;
    }

    const databaseInfo: DatabaseInfo = {
      databaseId,
      shouldEnableVoicebox: isVoiceboxToggleVisible
        ? isVoiceboxEnabledChecked
        : false,
      isPublishingSPG,
    };
    if (isVoiceboxQuestionsToggleVisible) {
      databaseInfo.isPublishingVoiceboxQuestions = isVoiceboxQuestionsChecked;
    }

    const props: ModelConfigPanelProps = {
      databaseInfo,
      spgInfo,
    };
    openPanel({ renderPanel: ModelConfigPanel, props });
  }, [
    databaseId,
    isPublishingSPG,
    isVoiceboxEnabledChecked,
    isVoiceboxQuestionsChecked,
    isVoiceboxQuestionsToggleVisible,
    isVoiceboxToggleVisible,
    nextButtonError,
    openPanel,
    spgInfo,
  ]);

  return (
    <>
      <div
        className={Classes.DRAWER_BODY}
        data-testid={TEST_IDS.publishProjectDrawer.database.container}
      >
        <PublishProjectDrawerStepper active="database" />

        <div className="sd-drawer-publish-project__database-config">
          <Label className="sd-label-switch">
            {
              copy.components.publishProjectDrawer.databaseConfiguration
                .databaseSectionHeader
            }
            <div className="sd-drawer-selector-menu">
              <DatabaseSelector
                creatable={canCreateDatabase}
                databaseId={databaseId}
                popoverPosition={Position.BOTTOM_LEFT}
                setDatabaseId={setDatabaseId}
              />
            </div>
          </Label>
        </div>

        {isVoiceboxToggleVisible ? (
          <div>
            <Tooltip
              content={
                copy.components.publishProjectDrawer.databaseConfiguration
                  .voiceboxSwitch.limitReached
              }
              disabled={Boolean(existingDatabase) || canToggleVoicebox}
            >
              <Switch
                alignIndicator={Alignment.RIGHT}
                checked={isVoiceboxEnabledChecked}
                className="sd-label-switch"
                data-testid={
                  TEST_IDS.publishProjectDrawer.database.voiceboxSwitch
                }
                disabled={!canToggleVoicebox}
                inline
                label={
                  copy.components.publishProjectDrawer.databaseConfiguration
                    .voiceboxSwitch.label
                }
                onChange={() =>
                  setShouldEnableVoicebox((prevValue) => !prevValue)
                }
              />
            </Tooltip>
          </div>
        ) : null}
        {isVoiceboxQuestionsToggleVisible ? (
          <div>
            <Switch
              alignIndicator={Alignment.RIGHT}
              checked={isVoiceboxQuestionsChecked}
              className="sd-label-switch"
              data-testid={
                TEST_IDS.publishProjectDrawer.database.voiceboxQuestionsSwitch
              }
              inline
              label={
                copy.components.publishProjectDrawer.databaseConfiguration
                  .voiceboxSwitch.questions
              }
              onChange={() =>
                setShouldPublishVoiceboxQuestions((prevValue) => !prevValue)
              }
            />
          </div>
        ) : null}

        {databaseId && Object.keys(spgInfo).length ? (
          <div>
            <ModelConfigSPG
              databaseId={databaseId}
              handleUpdateSPG={handleUpdateSPG}
              isPublishingSPG={isPublishingSPG}
              sensitivePropertyGroups={spgInfo}
              setIsPublishingSPG={setIsPublishingSPG}
            />
          </div>
        ) : null}
      </div>

      <PublishProjectDrawerFooter
        handleBack={handleCancel}
        handleNext={handleNext}
        nextError={nextButtonError}
        nextTestId={TEST_IDS.publishProjectDrawer.footer.nextToModelButton}
        nextText={copy.components.publishProjectDrawer.footer.next}
      />
    </>
  );
};
